import {PdsWidget} from './pds-widget';
import {ElementConfiguration} from './interfaces/element-configuration';
import { pdsLogger } from './pds-logger';
let log = pdsLogger.prependLogger(pdsLogger.requiresLogger('element-renderer'), 'pds-element-renderer.ts');

/**
 * Allows a polymer component to be rendered via a javaScript definition object.
 */
export class PdsElementRenderer extends PdsWidget {
  is = 'pds-element-renderer';

  //Input
  elementConfig: ElementConfiguration | null;

  properties = {
    elementConfig: {
      value: null,
      observer: 'elementConfigChange'
    }
  };

  /**
   * This is called from the observable.
   * @param newValue
   * @param oldConfig
   */
  elementConfigChange(newValue: ElementConfiguration | null, oldConfig: ElementConfiguration | null): void {
    log('elementConfig changed');
    this.renderElement(oldConfig);
  }

  /**
   * Creates a new element and inserts it into this component.
   */
  renderElement(previousConfig: ElementConfiguration | null): void {
    if (this.elementConfig == null) {
      this.innerHTML = '';
      log('clearing element');
      return;
    }

    let element: HTMLElement | null = <HTMLElement | null>this.firstChild;

    if (element == null || element.tagName.toUpperCase() !== this.elementConfig.tagName.toUpperCase()) {
      //Different or new element.
      log('creating element ' + this.elementConfig.tagName);
      this.innerHTML = '';
      element = document.createElement(this.elementConfig.tagName);
      this.applyAttributesAndProperties(element, previousConfig);
      this.appendChild(element);
    } else {
      //Same Element
      log('updating attributes and properties on existing element ' + this.elementConfig.tagName);
      this.applyAttributesAndProperties(element, previousConfig);
    }
  }

  /**
   * Copies the attributes and properties out of the ElementConfiguration and applies them to the passed in element.
   * @param element - The element to apply properties and attributes to.
   * @param previousConfig - The configuration object containing properties and attributes to apply
   */
  applyAttributesAndProperties(element: HTMLElement, previousConfig: ElementConfiguration | null): void {
    if (this.elementConfig == null || element == null) {
      return;
    }

    log('applying a config to ' + element.tagName);

    if (this.elementConfig.attributes != null) {
      for (let attributeName of Object.keys(this.elementConfig.attributes)) {
        element.setAttribute(attributeName, this.elementConfig.attributes[attributeName]);
      }

      //Now remove the previous attributes if they are not in the new value.
      if (previousConfig != null && previousConfig.attributes != null) {
        for (let attributeName of Object.keys(previousConfig.attributes)) {
          if (this.elementConfig.attributes[attributeName] == null) {
            element.removeAttribute(attributeName);
          }
        }
      }
    }

    if (this.elementConfig.properties != null) {
      Object.assign(element, this.elementConfig.properties);

      //Now remove the previous properties if they are not in the new value.
      if (previousConfig != null && previousConfig.properties != null) {
        for (let propertyName of Object.keys(previousConfig.properties)) {
          if (this.elementConfig.properties[propertyName] == null) {
            delete element[propertyName];
          }
        }
      }
    }

    if (this.elementConfig.scopeClass != null) {
      element.classList.add(this.elementConfig.scopeClass);
    }
  }
}
